import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, PlusCircle, AlertCircle } from 'lucide-react';
import { issueCreditNote } from '../../services/api/offlineSales/creditNotes';
import { getCustomers } from '../../services/api/offlineSales/customers';
import { getOrders } from '../../services/api/offlineSales/orders';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatCurrency } from '../../utils/orderCalculations';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import styles from './CreateCreditNotePage.module.css';

const CreateCreditNotePage = () => {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { success, error } = useAlert();

  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [originalOrderId, setOriginalOrderId] = useState('');
  const [sourceType, setSourceType] = useState('price_adjustment');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const customersResult = await getCustomers({ limit: 200 });
        setCustomers(customersResult.customers || []);
      } catch (err) {
        console.error('Failed to load credit note form data:', err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // Original Order is scoped to the selected customer only — a credit note must belong to
  // that customer's own order, not any order in the business.
  useEffect(() => {
    if (!customerId) {
      setOrders([]);
      setOriginalOrderId('');
      return;
    }
    (async () => {
      try {
        setIsLoadingOrders(true);
        const ordersResult = await getOrders({ customerId, limit: 200 });
        setOrders(ordersResult.orders || []);
      } catch (err) {
        console.error('Failed to load customer orders:', err);
      } finally {
        setIsLoadingOrders(false);
      }
    })();
    setOriginalOrderId('');
  }, [customerId]);

  const validateForm = () => {
    const nextErrors = {};

    if (!customerId) nextErrors.customerId = t('creditNote.customerRequired');
    if (!originalOrderId) nextErrors.originalOrderId = t('creditNote.originalOrderRequired');
    if (!amount || Number(amount) <= 0) nextErrors.amount = t('creditNote.amountMustBePositive');
    if (!reason.trim()) nextErrors.reason = t('creditNote.reasonRequired');

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) {
      error(t('creditNote.someFieldsRequired'), { title: t('common.validationError') });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        customerId: Number(customerId),
        originalOrderId: Number(originalOrderId),
        sourceType,
        amount: Number(amount),
        reason: reason.trim(),
      };

      const response = await issueCreditNote(payload);
      success(t('creditNote.creditNoteCreatedSuccessfully'), { title: t('common.success') });
      navigate(`/offline-sales/credit-notes/${response.creditNote.id}`);
    } catch (err) {
      console.error('Credit note creation error:', err);
      error(err.response?.data?.message || t('creditNote.failedToCreateCreditNote'), {
        title: t('common.error'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.createCreditNotePage}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <PlusCircle className={styles.headerIcon} size={22} />
            <div className={styles.titleContent}>
              <h1>{t('creditNote.createCreditNote')}</h1>
              <p>{t('creditNote.createCreditNoteDescription')}</p>
            </div>
          </div>
          <button type="button" onClick={() => navigate('/offline-sales/credit-notes')} className={styles.backButton}>
            <ArrowLeft size={16} />
            {t('creditNote.backToCreditNotes')}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className={styles.creditNoteForm}>
        <div className={styles.formSection}>
          <div className={styles.sectionHeader}>
            <PlusCircle className={styles.sectionIcon} size={20} />
            <h2>{t('creditNote.creditNoteDetails')}</h2>
          </div>

          <div className={styles.sectionBody}>
            <div className={styles.fieldsGrid}>
              <div className={styles.fieldGroup}>
                <label>{t('creditNote.customer')} *</label>
                <Dropdown
                  value={customerId}
                  onChange={value => setCustomerId(value || '')}
                  options={customers.map((customer) => ({ value: customer.id, label: customer.name }))}
                  placeholder={t('creditNote.selectCustomer')}
                  searchable
                  disabled={isLoading}
                  className={errors.customerId ? styles.error : ''}
                />
                {errors.customerId && <div className={styles.errorText}><AlertCircle size={14} /> {errors.customerId}</div>}
              </div>

              <div className={styles.fieldGroup}>
                <label>{t('creditNote.originalOrder')} *</label>
                <Dropdown
                  value={originalOrderId}
                  onChange={value => setOriginalOrderId(value || '')}
                  options={orders.map((order) => ({ value: order.id, label: `${order.orderNumber} (${formatCurrency(order.grandTotal)})` }))}
                  placeholder={t('creditNote.selectOriginalOrder')}
                  searchable
                  disabled={isLoading || isLoadingOrders || !customerId}
                  className={errors.originalOrderId ? styles.error : ''}
                />
                {errors.originalOrderId && <div className={styles.errorText}><AlertCircle size={14} /> {errors.originalOrderId}</div>}
              </div>
            </div>

            <div className={styles.fieldsGrid}>
              <div className={styles.fieldGroup}>
                <label>{t('creditNote.sourceType')}</label>
                <Dropdown
                  value={sourceType}
                  onChange={value => setSourceType(value || 'price_adjustment')}
                  options={[
                    { value: 'price_adjustment', label: t('creditNote.sourceType_price_adjustment') },
                    { value: 'goodwill', label: t('creditNote.sourceType_goodwill') },
                  ]}
                  allowClear={false}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>{t('creditNote.amount')} *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className={`${styles.input} ${errors.amount ? styles.error : ''}`}
                  placeholder={t('creditNote.amountPlaceholder')}
                />
                {errors.amount && <div className={styles.errorText}><AlertCircle size={14} /> {errors.amount}</div>}
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <label>{t('creditNote.reason')} *</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                className={`${styles.textarea} ${errors.reason ? styles.error : ''}`}
                placeholder={t('creditNote.reasonPlaceholder')}
              />
              {errors.reason && <div className={styles.errorText}><AlertCircle size={14} /> {errors.reason}</div>}
            </div>
          </div>
        </div>

        <div className={styles.formActions}>
          <button type="button" onClick={() => navigate('/offline-sales/credit-notes')} className={styles.secondaryButton}>
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={isSubmitting} className={styles.primaryButton}>
            {isSubmitting ? t('common.loading') : t('creditNote.create')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateCreditNotePage;
